/**
 * Deterministic Nutrition Engine — shared type contract.
 *
 * This module is PURE TypeScript: no Convex imports, no React imports, no I/O.
 * It is imported by BOTH the Convex backend (convex/) and the React frontend
 * (src/) so every calculated number in the product comes from one source of
 * truth. The AI chatbot must call these functions (via tools) and must never
 * invent the values they produce.
 */

/** Overall engine version. Bump when any algorithm contract changes. */
export const ENGINE_VERSION = "1.0.0";

/** Confidence levels used across all engine results. */
export type ConfidenceLevel = "learning" | "low" | "moderate" | "high";

/** Whether a value is an initial estimate or a data-personalised one. */
export type EstimateKind = "initial" | "personalised";

/** Provenance + explanation attached to every engine result. */
export interface EngineMeta {
  /** Overall engine version (ENGINE_VERSION). */
  readonly engineVersion: string;
  /** Version of the specific algorithm that produced this result. */
  readonly algorithmVersion: string;
  /** What was calculated, e.g. "bmi" | "bmr" | "initialTdee" | "macroTargets". */
  readonly kind: string;
  /** Epoch-ms when the calculation ran. */
  readonly computedAt: number;
  /** Initial vs personalised estimate. */
  readonly estimateKind: EstimateKind;
  /** Confidence in the result. */
  readonly confidence: ConfidenceLevel;
  /** Short, user-friendly plain-language explanation. */
  readonly explanation: string;
  /** Normalised inputs used for the calculation (for audit/history). */
  readonly inputs: Record<string, number | string | boolean | null>;
}

/** Standard wrapper: payload + provenance. */
export interface EngineResult<T> {
  readonly value: T;
  readonly meta: EngineMeta;
}

/** Metabolic sex option used by Mifflin-St Jeor. */
export type MetabolicSex = "male" | "female" | "unspecified";

/** Goal types supported by the engine (matches existing product values). */
export type EngineGoalType = "lose" | "maintain" | "gain" | "healthier" | "muscle";

/** Diet-plan aggression (product values preserved; labels shown as Comfortable/Moderate/Aggressive). */
export type DietAggression = "relaxed" | "steady" | "aggressive";

/** Activity levels (matches existing product values). */
export type ActivityLevel =
  | "sedentary"
  | "light"
  | "moderate"
  | "active"
  | "very_active";

/** Exercise frequency buckets (matches existing product values). */
export type ExerciseFrequency = "rarely" | "1-2" | "3-4" | "5+";

/** Safety flags that restrict automated programme generation. */
export type SafetyFlag =
  | "pregnancy"
  | "breastfeeding"
  | "under_18"
  | "underweight"
  | "eating_disorder"
  | "medical_restriction"
  | "medication_affecting_weight"
  | "rapid_unexplained_change";

/** Normalised profile inputs consumed by the engine. */
export interface EngineProfile {
  readonly weightKg?: number;
  readonly heightCm?: number;
  readonly age?: number;
  /** Raw product gender value: "male" | "female" | "prefer_not_to_say" | "other". */
  readonly gender?: string;
  readonly activityLevel?: string;
  readonly exerciseFrequency?: string;
  /** Free-form dominant exercise type, e.g. "resistance" | "cardio" | "mixed" | "none". */
  readonly exerciseType?: string;
  /** Average daily steps when known. */
  readonly avgDailySteps?: number;
  readonly goalType?: string;
  readonly targetWeightKg?: number;
  readonly weightLossPace?: string;
  readonly eatingStyle?: string;
  readonly dietType?: string;
  /** Optional user-supplied body-fat estimate (percent). Low confidence by default. */
  readonly bodyFatPercent?: number;
  /** Optional waist measurement in cm. */
  readonly waistCm?: number;
  /** Whether the user reports already dieting before starting. */
  readonly isDieting?: boolean;
  /** Self-reported previous average intake, when supplied. */
  readonly previousIntakeKcal?: number;
  /** Safety flags collected during onboarding/settings. */
  readonly safetyFlags?: readonly string[];
}

/** BMI classification buckets (standard adult ranges). */
export type BmiClass = "underweight" | "healthy" | "overweight" | "obesity";

export interface BmiValue {
  readonly bmi: number;
  readonly classification: BmiClass;
  readonly healthyRangeMinKg: number;
  readonly healthyRangeMaxKg: number;
}

export interface BmrValue {
  readonly bmrKcal: number;
  /** "mifflin_st_jeor" — default adult equation. */
  readonly equation: string;
  /** Optional lean-mass-based estimate when body-fat % was supplied. */
  readonly leanMassKcal?: number;
  readonly leanMassEquation?: string;
}

export interface ExpenditureValue {
  readonly kcalPerDay: number;
  readonly bmrKcal: number;
  readonly activityMultiplier: number;
  /** Steps-based adjustment applied to the multiplier (0 when unknown). */
  readonly stepsAdjustment: number;
}

/** Daily calorie programme target. */
export interface CalorieTargetValue {
  readonly targetKcal: number;
  /** Signed daily energy adjustment vs expenditure (negative = deficit). */
  readonly adjustmentKcal: number;
  /** Target weekly weight change in kg (signed; 0 for maintenance). */
  readonly weeklyChangeKg: number;
  /** Target weekly change as % of current body weight (signed). */
  readonly weeklyPercentOfBodyWeight: number;
  /** True when safety limits reduced the requested rate/deficit. */
  readonly wasConstrained: boolean;
  /** Why the target was constrained (empty when not constrained). */
  readonly constraintReasons: readonly string[];
  /** True when the programme is restricted due to safety flags (no deficit generated). */
  readonly restricted: boolean;
}

export interface MacroTargetsValue {
  readonly proteinG: number;
  readonly carbsG: number;
  readonly fatG: number;
  readonly fiberG: number;
  /** Reference body weight used for g/kg protein/fat calculations. */
  readonly proteinReferenceWeightKg: number;
  readonly proteinPerKg: number;
  readonly minFatG: number;
  readonly rationale: readonly string[];
}

/** Dynamic goal-duration projection (non-linear, Hall/NIH-inspired). */
export interface GoalDurationValue {
  /** Central estimate of weeks to goal (0 when maintaining/at goal). */
  readonly weeksToGoal: number;
  /** Optimistic bound of the range. */
  readonly weeksMin: number;
  /** Conservative bound of the range. */
  readonly weeksMax: number;
  /** Projected goal date range (epoch-ms), from computedAt. */
  readonly goalDateMin: number;
  readonly goalDateMax: number;
  /** Initial daily calorie target used by the projection. */
  readonly initialTargetKcal: number;
  /** Requested weekly rate before safety constraints (kg, signed). */
  readonly requestedWeeklyChangeKg: number;
  /** Effective weekly rate after safety constraints (kg, signed). */
  readonly effectiveWeeklyChangeKg: number;
  /** True when the timeline was extended to keep intake safe. */
  readonly extendedForSafety: boolean;
  /** True when the goal is not safely reachable (e.g. restricted). */
  readonly infeasible: boolean;
  readonly notes: readonly string[];
}

/** One point of a long-term weight forecast. */
export interface ForecastPoint {
  readonly weekIndex: number;
  readonly date: number;
  readonly projectedWeightKg: number;
  readonly intakeKcal: number;
  readonly expenditureKcal: number;
}

export interface ForecastValue {
  readonly points: readonly ForecastPoint[];
  readonly projectedGoalDate: number | null;
  readonly weeksToGoal: number | null;
}

/** Trend-weight state and output (robust smoothed weight). */
export interface TrendWeightState {
  readonly trendKg: number;
  /** Smoothed rate of change, kg per week (signed). */
  readonly driftKgPerWeek: number;
  /** Robust residual scale (kg) used for outlier resistance. */
  readonly sigmaKg: number;
  /** Number of observation days incorporated. */
  readonly observationCount: number;
  /** Date (epoch-ms day start) of the last observation. */
  readonly lastObservationDate: number;
}

export interface TrendWeightValue extends TrendWeightState {
  /** Whether the latest observation was treated as an outlier/downweighted. */
  readonly lastWasOutlier: boolean;
}

/** Data sufficiency assessment for adaptive updates. */
export interface DataSufficiency {
  readonly sufficient: boolean;
  readonly completeNutritionDays: number;
  readonly weighInCount: number;
  readonly reasons: readonly string[];
}

/** Personalised expenditure update outcome. */
export interface AdaptiveExpenditureValue {
  readonly expenditureKcal: number;
  readonly previousExpenditureKcal: number;
  /** Whether the estimate was updated this run. */
  readonly updated: boolean;
  /** Update factor actually applied (0 when paused). */
  readonly alphaApplied: number;
  readonly observedExpenditureKcal: number | null;
  readonly sufficiency: DataSufficiency;
  /** Why an update was accepted/limited/rejected — for the inspection view. */
  readonly decisionLog: readonly string[];
}

/** Weekly programme adjustment decision. */
export interface WeeklyAdjustmentValue {
  readonly previousTargetKcal: number;
  readonly newTargetKcal: number;
  readonly adjustmentKcal: number;
  readonly targetRateKgPerWeek: number;
  readonly actualRateKgPerWeek: number | null;
  readonly adjusted: boolean;
  readonly paused: boolean;
  readonly reasons: readonly string[];
}

/** Logging-day completeness classification. Never treat missing as zero. */
export type DayLogStatus = "complete" | "partial" | "not_logged" | "fasting";
